import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Calculator, Landmark, ShieldCheck, Sparkles, TrendingUp, Coins, PiggyBank, Repeat, Percent, Target } from "lucide-react";
import LoanEmiCalculator from "@/components/calculators/LoanEmiCalculator";
import FutureCostCalculator from "@/components/calculators/FutureCostCalculator";
import TermCoverCalculator from "@/components/calculators/TermCoverCalculator";
import SipCalculator from "@/components/calculators/SipCalculator";
import LumpsumCalculator from "@/components/calculators/LumpsumCalculator";
import FdCalculator from "@/components/calculators/FdCalculator";
import RdCalculator from "@/components/calculators/RdCalculator";
import CagrCalculator from "@/components/calculators/CagrCalculator";
import ReverseCagrCalculator from "@/components/calculators/ReverseCagrCalculator";
import { GLOSSARY } from "@/lib/glossary";

export default function Resources() {
  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-14">
          <div className="eyebrow mb-6">Resources</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink max-w-4xl">
            Hands-on calculators.<br />
            <span className="italic text-brass-deep">Plain-English</span> glossary.
          </h1>
          <p className="mt-8 text-[16px] md:text-[17px] text-ink-soft leading-relaxed max-w-3xl">
            Two parts to this page: interactive planning tools that update as you type, and a
            simple, jargon-free glossary of the mutual fund terms you'll come across. No sign-up,
            no downloads.
          </p>
        </div>
      </section>

      {/* Calculators */}
      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="eyebrow mb-4">Part A · Planning Tools</div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight max-w-3xl">
            Nine calculators, ledger-style.
          </h2>

          <Tabs defaultValue="emi" className="mt-10">
            <TabsList className="bg-paper border hairline p-1 h-auto flex flex-wrap gap-1" data-testid="resources-tabs">
              <TabsTrigger
                value="emi"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-emi"
              >
                <Landmark size={14} className="mr-2" /> Loan EMI
              </TabsTrigger>
              <TabsTrigger
                value="future"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-future"
              >
                <Calculator size={14} className="mr-2" /> Future Cost of Education & Healthcare
              </TabsTrigger>
              <TabsTrigger
                value="term"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-term"
              >
                <ShieldCheck size={14} className="mr-2" /> Right Term Cover
              </TabsTrigger>
              <TabsTrigger
                value="sip"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-sip"
              >
                <TrendingUp size={14} className="mr-2" /> SIP
              </TabsTrigger>
              <TabsTrigger
                value="lumpsum"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-lumpsum"
              >
                <Coins size={14} className="mr-2" /> Lumpsum
              </TabsTrigger>
              <TabsTrigger
                value="fd"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-fd"
              >
                <PiggyBank size={14} className="mr-2" /> FD
              </TabsTrigger>
              <TabsTrigger
                value="rd"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-rd"
              >
                <Repeat size={14} className="mr-2" /> RD
              </TabsTrigger>
              <TabsTrigger
                value="cagr"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-cagr"
              >
                <Percent size={14} className="mr-2" /> CAGR
              </TabsTrigger>
              <TabsTrigger
                value="rcagr"
                className="data-[state=active]:bg-ink data-[state=active]:text-paper-raised text-ink-soft px-4 py-2 text-[13px] tracking-wide"
                data-testid="tab-rcagr"
              >
                <Target size={14} className="mr-2" /> Reverse CAGR
              </TabsTrigger>
            </TabsList>

            <TabsContent value="emi" className="mt-8">
              <CalcHeader
                title="Loan EMI Calculator"
                sub="See your monthly instalment — and how much of a loan you actually repay once interest is added up."
              />
              <LoanEmiCalculator />
            </TabsContent>

            <TabsContent value="future" className="mt-8">
              <CalcHeader
                title="Future Cost of Education & Healthcare"
                sub="Enter today's cost for a course or procedure and see what it could cost by the time you need it, using realistic inflation assumptions."
              />
              <FutureCostCalculator />
            </TabsContent>

            <TabsContent value="term" className="mt-8">
              <CalcHeader
                title="Right Term Cover Calculator"
                sub="Check whether the standard 20× income cap is enough to cover your liabilities and goals."
              />
              <TermCoverCalculator />
            </TabsContent>

            <TabsContent value="sip" className="mt-8">
              <CalcHeader
                title="SIP Calculator"
                sub="Project the future value of a monthly Systematic Investment Plan across the full duration you're planning to invest."
              />
              <SipCalculator />
            </TabsContent>

            <TabsContent value="lumpsum" className="mt-8">
              <CalcHeader
                title="Lumpsum Calculator"
                sub="See what a one-time investment could grow to over your chosen duration at an assumed rate of return."
              />
              <LumpsumCalculator />
            </TabsContent>

            <TabsContent value="fd" className="mt-8">
              <CalcHeader
                title="FD Calculator"
                sub="Estimate the maturity value of a fixed deposit using your bank's interest rate and compounding frequency."
              />
              <FdCalculator />
            </TabsContent>

            <TabsContent value="rd" className="mt-8">
              <CalcHeader
                title="RD Calculator"
                sub="Work out what a recurring monthly deposit could grow to at maturity."
              />
              <RdCalculator />
            </TabsContent>

            <TabsContent value="cagr" className="mt-8">
              <CalcHeader
                title="CAGR Calculator"
                sub="Given what an investment started at and what it's worth now, find the annualized growth rate."
              />
              <CagrCalculator />
            </TabsContent>

            <TabsContent value="rcagr" className="mt-8">
              <CalcHeader
                title="Reverse CAGR Calculator"
                sub="Project the future value of an investment growing at an assumed compound annual rate."
              />
              <ReverseCagrCalculator />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Glossary */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="eyebrow mb-4">Part B · Mutual Fund Terms, Simplified</div>
              <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
                Investing shouldn't feel like<br />
                <span className="italic text-brass-deep">decoding a dictionary.</span>
              </h2>
              <p className="mt-6 text-[15px] text-ink-soft leading-relaxed">
                We keep your investing clean, crisp, and away from jargon, heavy legal language, and
                confusing acronyms, so you can focus on what really matters — your goals, your
                money, and your peace of mind.
              </p>
              <p className="mt-4 text-[14.5px] text-ink-soft leading-relaxed">
                Tap any term below to expand it and see what it means, why it matters, and a simple
                real-life example.
              </p>
            </div>

            <div className="md:col-span-8">
              <Accordion type="single" collapsible className="w-full" data-testid="glossary-accordion">
                {GLOSSARY.map((g, i) => (
                  <AccordionItem key={g.term} value={`g-${i}`} className="border-b hairline">
                    <AccordionTrigger
                      className="text-left py-5 hover:no-underline group"
                      data-testid={`glossary-trigger-${g.term.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="flex-1 pr-4">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <span className="num text-brass-deep text-[15px] tracking-wide">{g.term}</span>
                          <span className="text-ink-soft text-[13.5px]">— {g.full}</span>
                        </div>
                        <p className="text-[13.5px] text-ink-soft/90 leading-relaxed mt-1.5 pr-2">
                          {g.short}
                        </p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pr-6 space-y-4">
                      <Block label="What it means" body={g.meaning} />
                      <Block label="Example" body={g.example} accent />
                      <Block label="Why it matters" body={g.why} />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 card-paper p-8 md:p-10 ledger-rule">
            <div className="grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-8">
                <div className="chip mb-3">
                  <Sparkles size={12} /> NEW · 30-SECOND SNAPSHOT
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight">
                  Get your Planning Readiness Snapshot.
                </h3>
                <p className="text-[14.5px] text-ink-soft leading-relaxed mt-3">
                  Seven quick questions. We'll score your setup across savings, protection, and
                  emergency preparedness — and email you a one-page PDF summary.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link to="/snapshot" className="btn-brass" data-testid="resources-cta-snapshot">
                  Take the 30-Second Snapshot <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>

          {/* Secondary CTA */}
          <div className="mt-6 card-paper p-8 md:p-10 ledger-rule">
            <div className="grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-8">
                <div className="eyebrow mb-2">Still confused by the terms?</div>
                <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight">
                  You don't need to become a finance expert<br />to start investing well.
                </h3>
                <p className="text-[14.5px] text-ink-soft leading-relaxed mt-3">
                  We explain every step in simple language, help you understand what matters, and
                  keep your investing clean, crisp, and away from jargon and legal clutter.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <a
                  href="https://reliservepulse.com/book-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                  data-testid="resources-cta"
                >
                  Speak to Us in Plain English <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>

          <p className="mt-12 text-[12px] text-ink-soft/85 leading-relaxed max-w-4xl">
            All nine calculators above are illustrative planning tools that use the inputs and
            assumptions you provide. Actual returns vary; past performance is not indicative of
            future results. Mutual fund investments are subject to market risks — please read all
            scheme-related documents carefully before investing.
          </p>
        </div>
      </section>
    </div>
  );
}

function CalcHeader({ title, sub }) {
  return (
    <div className="mb-8">
      <h3 className="font-display text-2xl md:text-3xl text-ink">{title}</h3>
      <p className="text-[14.5px] text-ink-soft leading-relaxed mt-2 max-w-2xl">{sub}</p>
    </div>
  );
}

function Block({ label, body, accent }) {
  return (
    <div className={accent ? "border-l-2 border-brass pl-4" : ""}>
      <div className="eyebrow mb-1">{label}</div>
      <p className="text-[14.5px] text-ink leading-relaxed">{body}</p>
    </div>
  );
}
